import { useState, useRef, useCallback, useEffect } from 'react'
import mqtt, { type MqttClient } from 'mqtt'
import { getMqttBrokerUrl } from '../utils/mqttUrl'

export type MqttStatus = 'disconnected' | 'connecting' | 'connected' | 'error'

export interface MqttMessage {
  id: string
  timestamp: Date
  topic: string
  payload: Record<string, unknown>
}

interface UseMqttClientOptions {
  deviceId: string
  brokerUrl?: string
  autoConnect?: boolean
  maxMessages?: number
  onCommandReceived?: (properties: Record<string, unknown>) => void
  onReportReceived?: (properties: Record<string, unknown>) => void
}

export function useMqttClient({
  deviceId,
  brokerUrl = getMqttBrokerUrl(),
  autoConnect = true,
  maxMessages = 200,
  onCommandReceived,
  onReportReceived,
}: UseMqttClientOptions) {
  const [status, setStatus] = useState<MqttStatus>('disconnected')
  const [messages, setMessages] = useState<MqttMessage[]>([])
  const clientRef = useRef<MqttClient | null>(null)
  const messagesRef = useRef<MqttMessage[]>([])

  const addMessage = useCallback((topic: string, payload: Record<string, unknown>) => {
    const msg: MqttMessage = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      timestamp: new Date(),
      topic,
      payload,
    }
    messagesRef.current = [msg, ...messagesRef.current].slice(0, maxMessages)
    setMessages(messagesRef.current)
  }, [maxMessages])

  const connect = useCallback(() => {
    if (clientRef.current?.connected) return
    if (!deviceId) return

    setStatus('connecting')
    const client = mqtt.connect(brokerUrl, {
      clientId: `detail-${deviceId}-${Date.now()}`,
      clean: true,
    })

    client.on('connect', () => {
      setStatus('connected')
      // 同时订阅命令下发和设备上报两个主题
      client.subscribe(`iot/+/${deviceId}/commands/set`)
      client.subscribe(`iot/+/${deviceId}/properties/report`)
    })

    client.on('message', (_topic, payload) => {
      try {
        const msg = JSON.parse(payload.toString())
        const props = msg.properties || msg
        if (props && typeof props === 'object' && !Array.isArray(props)) {
          addMessage(_topic, props)
          if (_topic.endsWith('/commands/set') && onCommandReceived) {
            onCommandReceived(props)
          } else if (_topic.endsWith('/properties/report') && onReportReceived) {
            onReportReceived(props)
          }
        }
      } catch { /* ignore malformed JSON */ }
    })

    client.on('error', () => {
      setStatus('error')
    })

    client.on('close', () => {
      setStatus('disconnected')
    })

    clientRef.current = client
  }, [deviceId, brokerUrl, addMessage, onCommandReceived, onReportReceived])

  const disconnect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.end(true)
      clientRef.current = null
    }
    setStatus('disconnected')
  }, [])

  const clearMessages = useCallback(() => {
    messagesRef.current = []
    setMessages([])
  }, [])

  const publish = useCallback((topic: string, payload: Record<string, unknown>) => {
    if (clientRef.current?.connected) {
      clientRef.current.publish(topic, JSON.stringify(payload), { qos: 1 })
    }
  }, [])

  useEffect(() => {
    if (autoConnect && deviceId) {
      connect()
    }
    return () => {
      if (clientRef.current) {
        clientRef.current.end(true)
        clientRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId])

  return { status, messages, connect, disconnect, clearMessages, publish }
}
